import { Features } from "@storytell/cp-client";
import { getModelDisplayName } from "~/utils/modelMapping";
import { getModelVendor } from "~/utils/modelVendors";
import { usePromptBarContext } from "../PromptBarContext";

interface ModelOption {
  value: Features.Feature;
  label: string;
  description?: string;
}

interface ModelGroup {
  vendor: string;
  models: ModelOption[];
}

export const MODEL_GROUPS: ModelGroup[] = [
  {
    vendor: "storytell",
    models: [
      {
        value: Features.Feature.MODEL_STORYTELL_AUTO_SELECT,
        label: "Auto Select",
        description: "Let Storytell choose the best model",
      },
    ],
  },
  {
    vendor: "anthropic",
    models: [
      {
        value: Features.Feature.MODEL_ANTHROPIC_CLAUDE_4_SONNET_THINKING,
        label: "Claude 4 Sonnet",
        description: "Advanced reasoning capabilities",
      },
    ],
  },
  {
    vendor: "openai",
    models: [
      {
        value: Features.Feature.MODEL_OPENAI_GPT_5,
        label: "GPT-5",
        description: "Next-generation model",
      },
      {
        value: Features.Feature.MODEL_OPENAI_GPT_5_MINI,
        label: "GPT-5 Mini",
        description: "Balanced performance",
      },
      {
        value: Features.Feature.MODEL_OPENAI_GPT_5_NANO,
        label: "GPT-5 Nano",
        description: "Lightweight, fast",
      },
      {
        value: Features.Feature.MODEL_OPENAI_03,
        label: "o3",
        description: "Specialized reasoning model",
      },
    ],
  },
  {
    vendor: "google",
    models: [
      {
        value: Features.Feature.MODEL_GOOGLE_GEMINI_20_FLASH_LITE,
        label: "Gemini 2.0 Flash Lite",
        description: "Lightweight, fast",
      },
      {
        value: Features.Feature.MODEL_GOOGLE_GEMINI_25_FLASH,
        label: "Gemini 2.5 Flash",
        description: "Fast multimodal model",
      },
      {
        value: Features.Feature.MODEL_GOOGLE_GEMINI_25_PRO,
        label: "Gemini 2.5 Pro",
        description: "Advanced capabilities",
      },
    ],
  },
];

/**
 * Hook for model selection logic
 * Extracts all the business logic from ModelSelector component
 */
export const useModelSelectorLogic = () => {
  const { knowledgeSources } = usePromptBarContext();

  const currentModel = () => knowledgeSources.selectedModel() ?? Features.Feature.MODEL_STORYTELL_AUTO_SELECT;

  const currentVendor = () => getModelVendor(currentModel());

  const currentModelName = () => {
    // Try to get display name from mapping first
    const displayName = getModelDisplayName(currentModel());
    if (displayName) return displayName;

    // Fallback to finding in our groups
    for (const group of MODEL_GROUPS) {
      const model = group.models.find((m) => m.value === currentModel());
      if (model) return model.label;
    }

    return "Auto Select";
  };

  const selectModel = (modelValue: Features.Feature) => {
    knowledgeSources.setSelectedModel(modelValue);
  };

  const isModelSelected = (modelValue: Features.Feature) => {
    return currentModel() === modelValue;
  };

  return {
    currentModel,
    currentVendor,
    currentModelName,
    selectModel,
    isModelSelected,
    modelGroups: MODEL_GROUPS,
  };
};
